'use client';

import { Divider, Radio, UnstyledButton } from '@mantine/core';
import { Button } from '@scaleup/frontend/ui';
import {
  IconArrowLeft,
  IconArrowRight,
  IconAward,
  IconBriefcase,
  IconBuilding,
  IconBuildings,
  IconCalendar,
  IconCheck,
  IconCoinFilled,
  IconEdit,
  IconFile,
  IconFileCheck,
  IconFileX,
  IconMail,
  IconMapPin,
  IconPackages,
  IconPhone,
  IconTags,
  IconTagsFilled,
  IconTrophyFilled,
  IconUserCog,
  IconUsers,
  IconUserStar,
  IconWorldSearch,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { PropsWithChildren, useState } from 'react';
import { IntakeBase } from './common';
import { useSupplierIntakeContext } from './context';
import { NDrop } from './naics-drop';

const IconWrapper = ({ children }: PropsWithChildren) => (
  <div className="rounded-full h-[2.5rem] w-[2.5rem] flex items-center justify-center bg-primary-50">
    {children}
  </div>
);

export const Review = () => {
  const { setCurrentStep, supplier, setLastStep } = useSupplierIntakeContext();
  const [showDetails, setShowDetails] = useState(false);
  const [showCertifications, setShowCertifications] = useState(false);
  const [showNAICS, setShowNAICS] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const [showIndustry, setShowIndustry] = useState(false);

  const formatMoney = (value: number) => {
    if(value/(Math.pow(10, 6)) < 1) {
        return `$${value.toFixed(2)}`;
    }
    
    if( value/(Math.pow(10, 9)) < 1) {
      return `$${(value/(Math.pow(10, 6))).toFixed(1)} million`;
    }

    return `$${(value/(Math.pow(10, 9))).toFixed(1)} billion`;
  }
  const handleClick = async () => {
    setCurrentStep('final');
  };

  setLastStep('review');
  return (
    <IntakeBase currentStep={5}>
      <div className="flex flex-col gap-2">
        <p className="text-text-primary font-bold text-xl">Review & Submit</p>

        <p className="text-text-secondary">
          Please review all the information you've provided to ensure it's
          accurate and complete. If you need to make changes, you can go back to
          any section and update the information before submitting.
        </p>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <IconWrapper>
              <IconBuildings color="#4fb3cf" />
            </IconWrapper>
            <p className="text-text-primary font-bold text-lg pt-2">
              Company Details
            </p>
            <IconEdit
              className="ml-auto"
              color="#4fb3cf"
              onClick={() => setCurrentStep('details')}
            />
          </div>
          {showDetails && (
            <div className="flex flex-col m-3 ml-10 gap-2">
              <div className="flex flex-row gap-2">
                <IconBuildings color="#4fb3cf" />
                <p className="text-text-secondary text-sm ml-4">
                  {supplier.orgName}
                </p>
              </div>
              <div className="flex flex-row gap-2 mt-4">
                <IconMapPin color="#4fb3cf" />
                <p className="text-text-secondary text-sm ml-4">
                  {supplier.address1},{' '}
                  {`${supplier.address2 ? supplier.address2 + ',' : ''}`}{' '}
                  {supplier.city}, {supplier.state}, {supplier.zipCode}
                </p>
              </div>
              <div className="flex flex-row gap-2 mt-4">
                <IconMail color="#4fb3cf" />
                <p className="text-text-secondary text-sm ml-4">
                  {supplier.contactEmail}
                </p>
              </div>
              {supplier.contactPhone && (
                <div className="flex flex-row gap-2 mt-4">
                  <IconPhone color="#4fb3cf" />
                  <p className="text-text-secondary text-sm ml-4">
                    {supplier.contactPhone}
                  </p>
                </div>
              )}
              <div className="flex flex-row gap-2 mt-4">
                <IconWorldSearch color="#4fb3cf" />
                <p className="text-text-secondary text-sm ml-4">
                  {supplier.website}
                </p>
              </div>
              <div className="flex flex-col gap-8 pt-6 relative w-full border-t border-[#b8e1ec]">
                <div className="flex gap-8 items-center w-full">
                  <div className="flex flex-col gap-1 shrink-0 w-1/2">
                    <div className="flex items-center gap-4">
                      <div className="w-6 h-6 shrink-0">
                        <IconBriefcase color="#4fb3cf" />
                      </div>
                      <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                        Industry
                      </h3>
                    </div>
                    <div className="pl-10 py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.industry}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/2 shrink-0">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Primary Industry Sector
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.primaryIndustrySector}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1 pl-10 pb-5 w-full">
                  <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                    Company Description
                  </h3>
                  <div className="py-2">
                    <p className="text-[#667085] text-base font-normal leading-6 h-24">
                      {supplier.supplierData.description ||
                        'No description provided.'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-4 pl-10 w-[1056px]">
                  <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                    Products & Services
                  </h3>
                  <div className="flex gap-8 items-center">
                    <div className="flex flex-col gap-2 w-1/2">
                      {supplier.supplierData.productsAndServices
                        .filter(
                          (item, index) =>
                            index <=
                            supplier.supplierData.productsAndServices.length / 2
                        )
                        .map((item, index) => (
                          <p className="text-base leading-6">
                            <span className="text-[#475467] font-semibold">
                              {`${index + 1}.  `}
                            </span>
                            <span className="text-[#667085] font-normal">
                              {item}
                            </span>
                          </p>
                        ))}
                    </div>
                    <div className="flex flex-col gap-2 w-1/2">
                      {supplier.supplierData.productsAndServices
                        .filter(
                          (item, index) =>
                            index >
                            supplier.supplierData.productsAndServices.length / 2
                        )
                        .map((item, index) => (
                          <p className="text-base leading-6">
                            <span className="text-[#475467] font-semibold">
                              {`${index + Math.ceil(supplier.supplierData.productsAndServices.length / 2) + 1}.  `}
                            </span>
                            <span className="text-[#667085] font-normal">
                              {item}
                            </span>
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-8 items-center pl-10 w-full">
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Company Establishment Year
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.establishmentYear}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Locations Served (States, Regions, or Countries)
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.geographiesServed?.join(', ') ||
                          'No locations specified.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-8 items-center pl-10 w-full">
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Does the company have multiple sites?
                    </h3>
                    <div className="py-2">
                      <Radio.Group
                        value={
                          supplier.supplierData.multipleSites ? 'true' : 'false'
                        }
                      >
                        <div className="flex gap-8">
                          <Radio value="true" label="Yes" />
                          <Radio value="false" label="No" />
                        </div>
                      </Radio.Group>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Primary Business Activity
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.primaryBusinessActivity}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-8 items-center pl-10 w-full">
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Legal Organization Status
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.legalOrganizationStatus}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Name of Primary Shareholder
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.primaryShareholder}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-8 items-center pl-10 w-full">
                  <div className="flex flex-col gap-1 w-1/2">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Demographic Designations
                    </h3>
                    <div className="py-2">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.demographicDesignations?.join(
                          ', '
                        ) || 'No designations specified.'}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Main Contact Section */}
                <div className="flex flex-col gap-2 pt-6 relative w-full border-t border-[#b8e1ec]">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-6 h-6 shrink-0">
                      <IconUserStar color="#4fb3cf" />
                    </div>
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      Main Contact
                    </h3>
                  </div>
                  <div className="flex flex-col px-10">
                    <div className="w-full flex gap-4">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.mainContact.firstName}{' '}
                        {supplier.supplierData.mainContact.lastName}
                      </p>
                    </div>
                    <div className="w-full flex gap-4">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.mainContact.title}
                      </p>
                    </div>
                    <div className="w-full flex gap-4">
                      <p className="text-[#667085] text-base font-normal leading-6">
                        {supplier.supplierData.mainContact.email}
                      </p>
                    </div>
                    {supplier.supplierData.mainContact.phone && (
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.mainContact.phone}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {supplier.supplierData.keyContact1 && (
                  <div className="flex flex-col gap-2 pt-6 relative w-full">
                    <div className="flex items-center gap-4 w-full">
                      <div className="w-6 h-6 shrink-0">
                        <IconUsers color="#4fb3cf" />
                      </div>
                      <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                        Key Contact 1
                      </h3>
                    </div>
                    <div className="flex flex-col px-10">
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.keyContact1.firstName}{' '}
                          {supplier.supplierData.keyContact1.lastName}
                        </p>
                      </div>
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.keyContact1.title}
                        </p>
                      </div>
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.keyContact1.email}
                        </p>
                      </div>
                      {supplier.supplierData.keyContact1.phone && (
                        <div className="w-full flex gap-4">
                          <p className="text-[#667085] text-base font-normal leading-6">
                            {supplier.supplierData.keyContact1.phone}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {supplier.supplierData.keyContact2 && (
                  <div className="flex flex-col gap-2 pt-6 relative w-full">
                    <div className="flex items-center gap-4 w-full">
                      <div className="w-6 h-6 shrink-0">
                        <IconUsers color="#4fb3cf" />
                      </div>
                      <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                        Key Contact 2
                      </h3>
                    </div>
                    <div className="flex flex-col px-10">
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.keyContact2.firstName}{' '}
                          {supplier.supplierData.keyContact2.lastName}
                        </p>
                      </div>
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.keyContact2.title}
                        </p>
                      </div>
                      <div className="w-full flex gap-4">
                        <p className="text-[#667085] text-base font-normal leading-6">
                          {supplier.supplierData.keyContact2.email}
                        </p>
                      </div>
                      {supplier.supplierData.keyContact2.phone && (
                        <div className="w-full flex gap-4">
                          <p className="text-[#667085] text-base font-normal leading-6">
                            {supplier.supplierData.keyContact2.phone}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <Divider color="#b8e1ec" className="my-6" />
        <UnstyledButton
          className="w-full text-center"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? 'Hide Details △' : 'Show Details  ▽'}
        </UnstyledButton>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <IconWrapper>
              <IconUserStar color="#4fb3cf" />
            </IconWrapper>
            <p className="text-text-primary font-bold text-lg pt-2">
              Certifications & Compliance
            </p>
            <IconEdit
              className="ml-auto"
              color="#4fb3cf"
              onClick={() => setCurrentStep('demographics')}
            />
          </div>
          {showCertifications && (
            <div className="flex flex-col m-3 ml-10 gap-2">
              {supplier.supplierData.demographicCertifications
                .concat(supplier.supplierData.industryCertifications)
                .map((certification, index) => (
                  <div key={index} className="flex flex-col gap-2 mb-4 ">
                    <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                      {' '}
                      {certification.type}
                    </h3>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex flex-row gap-2">
                        <IconAward color="#4fb3cf" />
                        <p className="text-text-secondary text-sm ml-4">
                          {certification.name}
                        </p>
                      </div>
                      <div className="flex flex-row gap-2 mt-4">
                        <IconBuildings color="#4fb3cf" />
                        <p className="text-text-secondary text-sm ml-4">
                          {certification.agency ? certification.agency : 'N/A'}
                        </p>
                      </div>
                      <div className="flex flex-row gap-2 mt-4">
                        <IconCalendar color="#4fb3cf" />
                        <p className="text-text-secondary text-sm ml-4">
                          {certification.date
                            ? dayjs.unix(certification.date).format('MM/YYYY')
                            : 'Not specified'}
                        </p>
                      </div>
                      <div className="flex flex-row gap-2 mt-4">
                        <IconTags color="#4fb3cf" />
                        <p className="text-text-secondary text-sm ml-4">
                          {certification.number
                            ? certification.number
                            : 'Not specified'}
                        </p>
                      </div>
                      <div className="flex flex-row gap-2 mt-4">
                        <IconCheck color="#4fb3cf" />
                        <p className="text-text-secondary text-sm ml-4">
                          {certification.status
                            ? certification.status
                            : 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        <Divider color="#b8e1ec" className="my-6" />
        <UnstyledButton
          className="w-full text-center"
          onClick={() => setShowCertifications(!showCertifications)}
        >
          {showCertifications ? 'Hide Details △' : 'Show Details  ▽'}
        </UnstyledButton>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <IconWrapper>
              <IconBuildings color="#4fb3cf" />
            </IconWrapper>
            <p className="text-text-primary font-bold text-lg pt-2">
              Market & Industry
            </p>
            <IconEdit
              className="ml-auto"
              color="#4fb3cf"
              onClick={() => setCurrentStep('industry')}
            />
          </div>
          {showIndustry && (
            <div className="flex flex-col gap-8 w-full">
              <div className="grid grid-flow-col items-center pb-6 gap-3 w-full relative border-b border-[#b8e1ec]">
                <div className="flex flex-col gap-4 relative items-center">
                  <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                    Primary Customers
                  </h3>
                  <div className="flex flex-col gap-2 w-auto items-center">
                    {supplier.supplierData.customerTypes?.map(
                      (customer, index) => (
                        <p className="text-base leading-6 text-left w-full">
                          <span className="text-[#475467] font-semibold">{`${index + 1}. `}</span>
                          <span className="text-[#667085] font-normal">
                            {customer}
                          </span>
                        </p>
                      )
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4 relative border-r border-l border-[#b8e1ec] items-center">
                  <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                    Top 5 Industries Served
                  </h3>
                  <div className="flex flex-col gap-2 w-auto items-center">
                    {supplier.supplierData.top5Industries?.map(
                      (customer, index) => (
                        <p className="text-base leading-6 text-left w-full">
                          <span className="text-[#475467] font-semibold">{`${index + 1}. `}</span>
                          <span className="text-[#667085] font-normal">
                            {customer}
                          </span>
                        </p>
                      )
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4 relative items-center">
                  <h3 className="text-[#1d2939] text-base font-semibold leading-6">
                    Top 5 Revenue Channels
                  </h3>
                  <div className="flex flex-col gap-2 w-auto items-center">
                    {supplier.supplierData.primarySalesChannels?.map(
                      (customer, index) => (
                        <p className="text-base leading-6 text-left w-full">
                          <span className="text-[#475467] font-semibold">{`${index + 1}. `}</span>
                          <span className="text-[#667085] font-normal">
                            {customer}
                          </span>
                        </p>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Row - Existing Customers */}
              <div className="flex gap-8 items-center w-full">
                <div className="flex flex-col gap-4 flex-1">
                  <div className="text-[#1d2939] text-base font-semibold leading-6">
                    <span>Existing Customers </span>
                    <span className="text-sm font-normal leading-5 text-[#667085]">
                      (Companies we should exclude in the matching process.)
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    {supplier.supplierData.currentCustomers &&
                      supplier.supplierData.currentCustomers.length > 0 &&
                      supplier.supplierData.currentCustomers.map(
                        (customer, index) => (
                          <p key={customer} className="text-base leading-6">
                            <span className="text-[#475467] font-semibold">{`${index + 1}. `}</span>
                            <span className="text-[#667085] font-normal">
                              {customer}
                            </span>
                          </p>
                        )
                      )}
                    {(!supplier.supplierData.currentCustomers ||
                      supplier.supplierData.currentCustomers.length == 0) && (
                      <p>No Existing Customers provided</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Divider color="#b8e1ec" className="my-6" />
        <UnstyledButton
          className="w-full text-center"
          onClick={() => setShowIndustry(!showIndustry)}
        >
          {showIndustry ? 'Hide Details △' : 'Show Details  ▽'}
        </UnstyledButton>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <IconWrapper>
              <IconBuildings color="#4fb3cf" />
            </IconWrapper>
            <p className="text-text-primary font-bold text-lg pt-2">
              Company Structure
            </p>
            <IconEdit
              className="ml-auto"
              color="#4fb3cf"
              onClick={() => setCurrentStep('structure')}
            />
          </div>
          {showStructure && (
            <div className="flex flex-col m-3 ml-10 gap-2">
              <div className="flex flex-row items-center gap-2">
                <IconWrapper>
                  <IconUserCog color="#4fb3cf" />
                </IconWrapper>
                <h2 className="text-text-secondary text-base font-bold ml-4">
                  Leadership & Organizational Structure
                </h2>
              </div>
              <div className="grid grid-cols-10 mt-4">
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-text-secondary text-sm font-bold ml-4">
                    Number of Employees
                  </p>
                  <p className="text-text-secondary text-sm ml-4">
                    {supplier.supplierData.numberOfEmployees}
                  </p>
                </div>
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-text-secondary text-sm font-bold ml-4">
                    Number of Contractors
                  </p>
                  <p className="text-text-secondary text-sm ml-4">
                    {supplier.supplierData.numberOfContractors}
                  </p>
                </div>
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-text-secondary text-sm font-bold ml-4">
                    Does the company have a board of advisors?
                  </p>
                  <div className="py-2">
                    <Radio.Group
                      value={
                        supplier.supplierData.boardOfAdvisors ? 'true' : 'false'
                      }
                    >
                      <div className="flex gap-8 ml-4">
                        <Radio value="true" label="Yes" />
                        <Radio value="false" label="No" />
                      </div>
                    </Radio.Group>
                  </div>
                </div>
              </div>
              {supplier.supplierData.boardOfAdvisors &&
                supplier.supplierData.bOAExpectations && (
                  <div className="flex flex-col gap-2 mt-4">
                    <p className="text-text-secondary text-sm font-bold ml-4">
                      Expectations of Board of Directors/Advisors
                    </p>
                    {supplier.supplierData.bOAExpectations.map(
                      (expectation, index) => (
                        <p
                          key={index}
                          className="text-text-secondary text-sm ml-4"
                        >
                          <span className="text-[#475467] font-semibold">
                            {`${index + 1}. `}
                          </span>
                          <span className="text-[#667085] font-normal">
                            {expectation}
                          </span>
                        </p>
                      )
                    )}
                  </div>
                )}
              <div className="grid grid-cols-10 mt-4 mb-4">
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-text-secondary text-sm font-bold ml-4">
                    Ethnic Minority Employee Percentage
                  </p>
                  <p className="text-text-secondary text-sm ml-4">
                    {supplier.supplierData.ethnicMinorityPercentage}%
                  </p>
                </div>
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-text-secondary text-sm font-bold ml-4">
                    Women Employee Percentage
                  </p>
                  <p className="text-text-secondary text-sm ml-4">
                    {supplier.supplierData.womenEmployeePercentage}%
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-8 pt-6 relative w-full border-t border-[#b8e1ec]">
                <div className="flex flex-row items-center gap-2">
                  <IconWrapper>
                    <IconCoinFilled color="#4fb3cf" />
                  </IconWrapper>
                  <h2 className="text-text-secondary text-base font-bold ml-4">
                    Financial Structure
                  </h2>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">I/we would rather own 100% of a $30M company than 60% of a $100M company.</p>
                    <p className="text-text-secondary text-sm ml-4">{supplier.supplierData.ownershipQuestion}</p>
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">Funding Sources <span className='font-normal text-xs'>(Types of financing the company has utilized)</span></p>
                    {supplier.supplierData.financingUsed?.map((financing, index) => (<p className="text-text-secondary text-sm ml-4" key={financing}><span className='font-bold'>{`${index+1}. `}</span> {financing}</p>))}
                  </div>
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">Financing Costs <span className='font-normal text-xs'>(Estimated expenses associated with borrowed funds)</span></p>
                    <p className="text-text-secondary text-sm ml-4">{supplier.supplierData.costOfFinancing}</p>
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-7">
                    <p className="text-text-secondary text-sm font-bold ml-4">Purpose of External Funding</p>
                    {supplier.supplierData.useForOutsideCapital?.map((financing, index) => (<p className="text-text-secondary text-sm ml-4" key={financing}><span className='font-bold'>{`${index+1}. `}</span> {financing}</p>))}
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-7">
                    <p className="text-text-secondary text-sm font-bold ml-4">Current Revenue Range</p>
                    <p className="text-text-secondary text-sm ml-4" >{supplier.supplierData.currentRevenueRange}</p>
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-7">
                    <p className="text-text-secondary text-sm font-bold ml-4">Annual Revenue</p>
                    <p className="text-text-secondary text-sm ml-4" >Annual revenue for the last 3 years.</p>
                  </div>
                </div>
                {supplier.supplierData.yearlyTotalRevenue?.map((revenue, index) => (
                    <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-7">
                    <p className="text-text-secondary text-sm font-bold ml-4">Total Revenue for {revenue.year}</p>
                    <p className="text-text-secondary text-sm ml-4" >{formatMoney(revenue.revenue)}</p>
                  </div>
                </div>
                ))}
              </div>
              <div className="flex flex-col gap-8 pt-6 relative w-full border-t border-[#b8e1ec]">
                <div className="flex flex-row items-center gap-2">
                  <IconWrapper>
                    <IconPackages color="#4fb3cf" />
                  </IconWrapper>
                  <h2 className="text-text-secondary text-base font-bold ml-4">
                    Service Offerings
                  </h2>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">Digital Growth Needs</p>
                    <p className="text-text-secondary text-sm ml-4">What are the top three digital products your company needs to grow?</p>
                    {supplier.supplierData.digitalProducts?.map((need, index) => (<p className="text-text-secondary text-sm ml-8" key={need}><span className='font-bold'>{`${index+1}. `}</span> {need}</p>))}
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">Outsourced Services</p>
                    <p className="text-text-secondary text-sm ml-4">What are the top three third-party/outsourced services your company needs to grow?</p>
                    {supplier.supplierData.outsourcedServices?.map((need, index) => (<p className="text-text-secondary text-sm ml-8" key={need}><span className='font-bold'>{`${index+1}. `}</span> {need}</p>))}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-8 pt-6 relative w-full border-t border-[#b8e1ec]">
                <div className="flex flex-row items-center gap-2">
                  <IconWrapper>
                    <IconTrophyFilled color="#4fb3cf" />
                  </IconWrapper>
                  <h2 className="text-text-secondary text-base font-bold ml-4">
                    Business Credentials
                  </h2>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-9">
                    <p className="text-text-secondary text-sm font-bold ml-4">Capability Statement <span className='text-xs font-normal'>(A document highlighting the company's expertise, accomplishments, and unique value proposition.)</span></p>
                    {supplier.supplierData.capabilitySheet && <div className="text-sm ml-8 flex flex-row bg-[#D3ECF3] rounded-xl w-fit items-center p-3 text-[#377D91] underline" ><IconFileCheck color='#377D91' />{supplier.supplierData.capabilitySheet.fileKey?.split("/")[1]}</div>}
                    {!supplier.supplierData.capabilitySheet && <div className="text-sm ml-8 flex flex-row bg-[#D3ECF3] rounded-xl w-fit items-center p-3 text-[#377D91]" ><IconFileX color='#377D91' />No Capability Sheet uploaded</div>}
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">Awards & Recognitions</p>
                    {supplier.supplierData.awards?.map((award, index) => (<p className="text-text-secondary text-sm ml-4" key={index}><span className='font-bold'>{`${index+1}. `}</span> {award.name} - {award.year}</p>))}
                  </div>
                </div>
                <div className="grid grid-cols-10 mt-4">
                  <div className="flex flex-col gap-2 col-span-5">
                    <p className="text-text-secondary text-sm font-bold ml-4">Past Clients</p>
                    {supplier.supplierData.previousCustomers?.map((customer, index) => (<p className="text-text-secondary text-sm ml-4" key={index}><span className='font-bold'>{`${index+1}. `}</span> {customer}</p>))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Divider color="#b8e1ec" className="my-6" />
        <UnstyledButton
          className="w-full text-center"
          onClick={() => setShowStructure(!showStructure)}
        >
          {showStructure ? 'Hide Details △' : 'Show Details  ▽'}
        </UnstyledButton>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <IconWrapper>
              <IconTags color="#4fb3cf" />
            </IconWrapper>
            <p className="text-text-primary font-bold text-lg pt-2">
              NAICS Codes
            </p>
            <IconEdit
              className="ml-auto"
              color="#4fb3cf"
              onClick={() => setCurrentStep('naics')}
            />
          </div>
          {showNAICS && (
            <div className="flex flex-col m-3 ml-10 gap-2">
              <div className="flex flex-col gap-8 pt-6 relative w-full">
                <div className="flex gap-8 items-center w-full">
                  <div className="flex flex-col gap-1 w-1/2 shrink-0">
                    <div className="py-2">
                      <NDrop disabled={true}/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Divider color="#b8e1ec" className="my-6" />
        <UnstyledButton
          className="w-full text-center"
          onClick={() => setShowNAICS(!showNAICS)}
        >
          {showNAICS ? 'Hide Details △' : 'Show Details  ▽'}
        </UnstyledButton>
      </div>

      <div className="flex justify-between">
        <Button
          text="Back"
          variant="secondary"
          prefixIcon={<IconArrowLeft />}
          onClick={() => setCurrentStep('naics')}
        />

        <Button
          text="Submit"
          variant="primary"
          suffixIcon={<IconArrowRight />}
          onClick={handleClick}
        />
      </div>
    </IntakeBase>
  );
};
